import { useCallback, useEffect, useRef, useState } from 'react';
import { equipBoostAnimation, fetchBoostAnimations } from '../_helpers/boostAnimations.js';

/**
 * The listener's boost animations for the Boost desk (ENG-814, FX-7): the
 * owned ones and the one worn, read from the skin shelf when `enabled` turns
 * on (each time the desk opens, so a Wear on the shelf shows here).
 *
 * `pick(item)` equips it (`PUT /me/skins/equipped/boost_animation`). There is
 * no server frame for it: after the 2xx the worn one is `item`, and the
 * server plays it on this listener's next boost for everyone. A refused pick
 * keeps the one worn and reports `error` (the fetchWrapper error); a failed
 * read is `status` 'error', and `retry()` reads again.
 *
 * Returns `{ status, items, equippedId, equipped, busyId, error, retry, pick }`,
 * `status` being 'idle' | 'loading' | 'ready' | 'error'.
 */
export const useBoostAnimations = ({ enabled = false } = {}) => {
  const [list, setList] = useState({ status: 'idle', items: [], equippedId: null, error: null });
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const busyRef = useRef(false);
  const readRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    readRef.current += 1;
    const read = readRef.current;
    setList((current) => ({ ...current, status: 'loading', error: null }));
    fetchBoostAnimations()
      .then(({ items, equippedId }) => {
        if (readRef.current === read) setList({ status: 'ready', items, equippedId, error: null });
      })
      .catch((err) => {
        if (readRef.current === read) setList((current) => ({ ...current, status: 'error', error: err ?? { status: 500 } }));
      });
    return () => { readRef.current += 1; };
  }, [enabled, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const pick = useCallback((item) => {
    if (!item?.id || busyRef.current) return Promise.resolve(false);
    busyRef.current = true;
    setBusyId(item.id);
    setError(null);
    return equipBoostAnimation(item.id)
      .then(() => {
        setList((current) => ({ ...current, equippedId: item.id }));
        return true;
      })
      .catch((err) => {
        setError(err ?? { status: 500 });
        return false;
      })
      .finally(() => {
        busyRef.current = false;
        setBusyId(null);
      });
  }, []);

  const equipped = list.items.find((item) => item.id === list.equippedId) ?? null;

  return {
    status: list.status,
    items: list.items,
    equippedId: list.equippedId,
    equipped,
    busyId,
    error,
    retry,
    pick,
  };
};

export default useBoostAnimations;
